import { Link } from "react-router-dom";
import { COMPANY } from "../../data/company";
import SEO from "../../components/SEO";
import ServiceGallerySection from "../../components/ServiceGallerySection";
import ContactForm from "../../components/ContactForm";
import { imageUrl } from "../../lib/images";

export default function TerracesOutdoor() {
  return (
    <>
      <SEO
        title="Træterrasser og udendørsarealer"
        description={`Eksklusive træterrasser og udendørsarealer på Fyn af ${COMPANY.name} i ${COMPANY.city}. Kvalitetstømrer til terrassebyggeri, trapper og uderum. Ring ${COMPANY.phone}.`}
        path="/ydelser/traeterrasser-og-udendoersarealer"
        image="/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg"
      />

      {/* Hero header */}
      <section className="bg-[#111111] text-white py-16 sm:py-24 border-b border-neutral-800 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg", "width=1920,quality=80,format=auto")}
            alt="Eksklusiv træterrasse og udendørsareal udført af tømrer"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-[#111111]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <Link to="/ydelser" className="hover:underline">Ydelser</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Træterrasser & udendørsarealer</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Træterrasser & udendørsarealer
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              En velbygget træterrasse forlænger sommeren og binder hus og have harmonisk sammen. Hos <strong>{COMPANY.name}</strong> bygger vi solide, smukke udemiljøer og terrasser i holdbare træsorter, tilpasset boligens arkitektur og terrænets linjer.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={COMPANY.phoneLink}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#A78E51] text-white font-extrabold text-sm hover:bg-[#937B43] transition-colors"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring direkte: {COMPANY.phone}</span>
              </a>
              <a
                href="#kontakt-sektion"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-white/10 hover:bg-white/20 text-white font-bold text-sm border border-neutral-600 transition-colors"
              >
                <span>Få tilbud på træterrasse</span>
                <i className="fa-solid fa-arrow-down text-xs text-[#A78E51]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-neutral-700 leading-relaxed">
              <div className="inline-block px-3 py-1 bg-amber-50 text-[#A78E51] rounded text-xs font-bold uppercase tracking-wider">
                Gedigent udemiljø
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111]">
                Skræddersyede uderum skabt til udeliv og hygge
              </h2>
              <p>
                En terrasse i træ er mere end bare et plant underlag – det er familiens samlingspunkt i hele den lune halvdel af året. Korrekt dimensioneret underkonstruktion, god ventilation under brædderne og præcise klinke- eller retkantede samlinger er altafgørende for, at terrassen holder sig plan, smuk og stabil i årtier.
              </p>
              <p>
                Vi rådgiver dig om de bedste materialer til dit projekt – hvad enten du drømmer om eksklusivt hårdttræ (Ipé, Cumaru), varmebehandlet termotræ, lærketræ eller trykimprægneret fyr med lang levetid. Vi sikrer også integrerede niveauforskelle, elegante trapper, indbyggede bænke og læhegn, der passer perfekt til din have.
              </p>

              {/* Bullet Points */}
              <div className="pt-4 space-y-3">
                <h3 className="font-bold text-lg text-[#111111]">
                  Vores ekspertise inden for uderum dækker:
                </h3>
                <ul className="space-y-2.5">
                  {[
                    "Opbygning af nye hævede og terrænnære træterrasser",
                    "Dimensionering af bærende strø- og stolpekonstruktioner i hårdttræ og tryk",
                    "Integrerede trapper, ramper og niveauskift i terræn",
                    "Læhegn, afskærmning, rækværk og pergolaer i matchende træ",
                    "Udskiftning og renovering af udtjente terrassebrædder",
                    "Præcis montering med skjulte skruer eller synlige ensartede skruerækker",
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-[#A78E51] shrink-0 mt-0.5">
                        <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                      </div>
                      <span className="text-neutral-800 text-sm sm:text-base font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Card / Fast facts */}
            <div className="lg:col-span-5 bg-neutral-50 rounded-xl p-8 border border-neutral-200 shadow-sm space-y-6">
              <h3 className="text-xl font-bold text-[#111111] border-b border-neutral-200 pb-4">
                Hvorfor vælge os til din træterrasse?
              </h3>

              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-compass-drafting" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Solidt fundament & ventilation</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Korrekt fald, tilstrækkelig luftcirkulation og punktfundamenter, der forhindrer fugtophobning og råd.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-tree" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Rådgivning om træsorter</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Vi hjælper dig med at vælge træ efter budget, vedligeholdelsesønske og æstetisk udtryk.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-[#111111] text-[#A78E51] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-ruler-combined" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#111111] text-sm">Præcise snit og pæne kanter</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Ingen sjuskede afslutninger. Vi lægger vægt på rene linjer, proppede skruehuller eller skjult montage.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-200">
                <div className="bg-white p-4 rounded-lg border border-neutral-200 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-neutral-500 block">Kontakt tømrermesteren</span>
                    <span className="font-bold text-sm text-[#111111]">{COMPANY.name}</span>
                  </div>
                  <a
                    href={COMPANY.phoneLink}
                    className="px-3.5 py-2 bg-[#A78E51] hover:bg-[#937B43] text-white text-xs font-bold rounded transition-colors"
                  >
                    Ring op
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery linked to CMS slug 'traeterrasser-og-udendoersarealer' */}
      <ServiceGallerySection
        gallerySlug="traeterrasser-og-udendoersarealer"
        fallbackTitle="Træterrasser & udendørsarealer"
        subtitle="Billeder af udførte terrasser og udemiljøer på Fyn"
        fallbackImages={[
          "/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg",
          "/_assets/traeterrasse-udendoers-havemiljoe.jpg",
          "/_assets/doere-vinduer-terrasse.jpg",
        ]}
      />

      {/* Contact Form Section */}
      <section id="kontakt-sektion" className="py-16 sm:py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-[#A78E51] text-xs font-bold uppercase tracking-widest block mb-2">
              Få et uforpligtende tilbud
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Klar til ny træterrasse eller uderum?
            </h2>
            <p className="mt-3 text-neutral-400 text-sm sm:text-base">
              Send os en besked med dine tanker eller mål for projektet, så vender vi hurtigt tilbage med faglig sparring og et godt tilbud.
            </p>
          </div>

          <div className="bg-white text-neutral-900 rounded-xl p-6 sm:p-10 shadow-xl">
            <ContactForm
              defaultService="Træterrasser og udendørsarealer"
              heading="Forespørgsel på træterrasse"
            />
          </div>
        </div>
      </section>
    </>
  );
}
